import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useNotifications } from "./NotificationContext";
import { useTheme } from "../Theme/ThemeContext";

export default function NotificationBell() {
  const { currentTheme } = useTheme();
  const {
    isSupported,
    permission,
    isEnabled,
    newJobsCount,
    toggleNotifications,
    clearNewJobsCount,
  } = useNotifications();

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleEnableNotifications = async () => {
    await toggleNotifications();
  };

  const handleViewJobs = () => {
    clearNewJobsCount();
    setShowDropdown(false);
  };

  if (!isSupported) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 rounded-xl text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700/50 transition-colors"
        aria-label="Notifications"
      >
        <i className={`ri-notification-3-line text-xl ${newJobsCount > 0 ? "animate-pulse" : ""}`}></i>
        
        {/* Badge */}
        <AnimatePresence>
          {newJobsCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full"
            >
              {newJobsCount > 99 ? "99+" : newJobsCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-80 bg-white dark:bg-neutral-800 rounded-2xl shadow-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden z-50"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 dark:text-white">Notifications</h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEnabled && permission === "granted"}
                    onChange={handleEnableNotifications}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-neutral-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent-500 dark:peer-focus:ring-accent-600 rounded-full peer dark:bg-neutral-600 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-neutral-600 peer-checked:bg-accent-600"></div>
                </label>
              </div>
              {permission === "denied" && (
                <p className="text-xs text-red-500 mt-1">
                  Notifications blocked. Enable in browser settings.
                </p>
              )}
              {permission === "default" && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Click toggle to enable job alerts
                </p>
              )}
            </div>

            {/* Content */}
            <div className="p-4">
              {!isEnabled || permission !== "granted" ? (
                <div className="text-center py-6">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${currentTheme.colors.gradient} flex items-center justify-center`}>
                    <i className="ri-notification-3-line text-2xl text-white"></i>
                  </div>
                  <h4 className="font-semibold text-neutral-900 dark:text-white mb-2">
                    Stay Updated!
                  </h4>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                    Get notified instantly when new jobs matching your interests are posted.
                  </p>
                  <button
                    onClick={handleEnableNotifications}
                    className={`w-full px-4 py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 transition-opacity`}
                  >
                    Enable Notifications
                  </button>
                </div>
              ) : newJobsCount > 0 ? (
                <div>
                  <div className="flex items-center gap-3 p-3 bg-accent-50 dark:bg-accent-900/20 rounded-xl mb-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentTheme.colors.gradient} flex items-center justify-center flex-shrink-0`}>
                      <i className="ri-briefcase-line text-white"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-neutral-900 dark:text-white">
                        {newJobsCount} New Job{newJobsCount > 1 ? "s" : ""}!
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        Fresh opportunities waiting for you
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/jobs"
                    onClick={handleViewJobs}
                    className={`block w-full px-4 py-2.5 rounded-xl text-center text-white font-semibold bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 transition-opacity`}
                  >
                    View Jobs
                  </Link>
                </div>
              ) : (
                <div className="text-center py-6">
                  <i className="ri-checkbox-circle-line text-4xl text-emerald-500 mb-2"></i>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    You're all caught up! We'll notify you when new jobs are posted.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
